import { LOCKDOWN_ORDER, type Block, type BlockType, type Capabilities, type Layout } from '@kioskforge/shared';

/**
 * Blocks an ESP32 panel cannot run (no typing, no iframe, no server-side quiz:
 * BUILD.md §4 block table). Device class is enforced here in addition to the
 * device's declared `capabilities.blocks`, so a mis-reported capability list
 * cannot smuggle a `form` onto a panel.
 *
 * The §3 widgets are added wholesale: every widget is `tablet_only` until its
 * server SVG renderer exists (PLATFORM_EXPANSION.md §3 / rule 9), and the ESP32
 * SVG→sharp renderer is later work (b7). Clear a widget from this set only when
 * its panel renderer ships; the React side carries the same badge.
 */
const ESP32_UNSUPPORTED: ReadonlySet<string> = new Set([
  'form',
  'consent',
  'web_embed',
  'quiz',
  'clock',
  'weather',
  'news',
  'sport',
  'solar',
  'calendar',
  'slideshow',
  'departures',
  'energy',
  'notice',
  'qr'
]);

export type CapabilityResult = { ok: true } | { ok: false; reasons: string[] };

function needsTouch(block: Block): boolean {
  if (block.on?.tap?.length) return true;
  if (block.type === 'live_tile' && 'toggle' in block.props && block.props.toggle) return true;
  return false;
}

/**
 * Can one device run one block? The single source of the per-block rules the
 * deploy gate (`layoutRunsOn`) and the admin's capability-aware preview (A5b.12)
 * both use — so the preview can never disagree with what a deployment will do.
 * Checks the device's declared block list, the per-class ESP32 limits and whether
 * the block needs touch. `screenId` is optional: pass it to get the same
 * `screen "x" block "y": …` phrasing the deployment gate returns.
 */
export function blockRunsOn(
  block: Block,
  capabilities: Capabilities,
  screenId?: string
): CapabilityResult {
  const reasons: string[] = [];
  const where = screenId ? `screen "${screenId}" block "${block.id}"` : `block "${block.id}"`;
  if (!capabilities.blocks.includes(block.type as BlockType)) {
    reasons.push(`${where}: block type "${block.type}" is not in the device's supported block list.`);
  }
  if (capabilities.device_class === 'esp32' && ESP32_UNSUPPORTED.has(block.type)) {
    reasons.push(`${where}: block type "${block.type}" cannot run on an ESP32 panel.`);
  }
  if (needsTouch(block) && !capabilities.touch) {
    reasons.push(`${where}: block requires touch but the device reports touch = false.`);
  }
  return reasons.length ? { ok: false, reasons } : { ok: true };
}

function screenBlocks(layout: Layout): Array<{ screenId: string; block: Block }> {
  return layout.screens.flatMap((screen) => screen.blocks.map((block) => ({ screenId: screen.id, block })));
}

/**
 * Server-side deployment gate (BUILD.md §7): can this device run this layout?
 * Checks block support (both the device's declared list and the per-class
 * limits), whether any block needs touch, and — when the caller passes the
 * preset's `minLockdown` — that the device's lockdown is high enough.
 * Returns every failure reason, not just the first, so the admin gets one list.
 */
export function layoutRunsOn(
  layout: Layout,
  capabilities: Capabilities,
  minLockdown?: keyof typeof LOCKDOWN_ORDER
): CapabilityResult {
  const reasons: string[] = [];

  if (minLockdown && LOCKDOWN_ORDER[capabilities.lockdown] < LOCKDOWN_ORDER[minLockdown]) {
    reasons.push(`Device lockdown "${capabilities.lockdown}" is below the required "${minLockdown}".`);
  }

  for (const { screenId, block } of screenBlocks(layout)) {
    const result = blockRunsOn(block, capabilities, screenId);
    if (!result.ok) reasons.push(...result.reasons);
  }

  return reasons.length ? { ok: false, reasons } : { ok: true };
}
